/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { JSX, ComponentProps, ComponentChildren } from 'preact';
import { Text } from '../UNSAFE_Text';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';

type IntrinsicProps = Pick<JSX.HTMLAttributes<HTMLDivElement>, 'children'>;
type Props = {
  children?: ComponentChildren;
  /**
   * Name of the text slot which determines styling
   */
  variant: 'primary' | 'secondary' | 'tertiary' | 'quaternary' | 'overline' | 'metadata';
} & IntrinsicProps;

const textVariants: Record<Props['variant'], ComponentProps<typeof Text>['variant']> = {
  primary: 'primary',
  secondary: 'primary',
  tertiary: 'secondary',
  quaternary: 'secondary',
  overline: 'secondary',
  metadata: 'secondary'
};

const textSizes: Record<Props['variant'], ComponentProps<typeof Text>['size']> = {
  primary: 'md',
  secondary: 'sm',
  tertiary: 'xs',
  quaternary: 'sm',
  overline: 'xs',
  metadata: 'sm'
};

const textLineClamps: Record<Props['variant'], ComponentProps<typeof Text>['lineClamp']> = {
  primary: undefined,
  secondary: 2,
  tertiary: 2,
  quaternary: 2,
  overline: 1,
  metadata: 1
};

export function ListItemText({ children, variant }: Props) {
  const { isTabbable } = useTabbableMode();

  const textContent = (
    <Text
      variant={textVariants[variant]}
      size={textSizes[variant]}
      lineClamp={textLineClamps[variant]}>
      {children}
    </Text>
  );

  return isTabbable ? <div tabIndex={0}>{textContent}</div> : textContent;
}
